import type { AdminRecord, EntityRecord } from '@/types/record'
import { isoDaysAgo, isoHoursAgo } from '@/utils/format'
import { mulberry32, pick, pickBy, intBetween } from '@/utils/seed'

export interface RegionSeed extends EntityRecord {
  code: string
  name: string
  level: 'province' | 'city' | 'district'
  level_label: string
  parent_id: string
  parent_name: string
  path: string
  sort: number
  status: 'active' | 'disabled'
  resource_count: number
  admin_scope_count: number
}

export interface UserSeed extends EntityRecord {
  user_no: string
  name: string
  phone: string
  birth_date: string
  gender: 'male' | 'female'
  region_id: string
  region_name: string
  region_path: string
  family_id: string
  family_name: string
  family_role: string
  family_type: string
  age: number
  status: 'pending_verification' | 'active' | 'disabled' | 'cancelled'
  risk_level: '低风险' | '中风险' | '高风险'
  tags: string[]
  device_bound: boolean
  address: string
  last_active_at: string
  registered_at: string
  height_cm: number
  weight_kg: number
  blood_type: string
  medical_history: string
  allergy_history: string
  emergency_contact: string
  consent_scope: string[]
}

export interface FamilySeed extends EntityRecord {
  family_no: string
  name: string
  owner_user_id: string
  owner_name: string
  family_type: string
  region_id: string
  region_name: string
  region_path: string
  address: string
  member_count: number
  guardian_count: number
  risk_level: '低风险' | '中风险' | '高风险'
  status: 'active' | 'disabled'
  created_at: string
}

export interface DrugSeed extends EntityRecord {
  drug_no: string
  generic_name: string
  trade_name: string
  approval_no: string
  barcode: string
  category_id: string
  category_name: string
  otc_type: '处方药' | '非处方药'
  dosage_form: string
  spec: string
  unit: string
  manufacturer: string
  indication: string
  usage: string
  precautions: string
  stock: number
  safety_stock: number
  price_fen: number
  review_status: 'approved' | 'pending' | 'rejected' | 'offline'
  image_name: string
  cover_color: string
  created_at: string
  updated_at: string
}

export interface CareSeed extends EntityRecord {
  name: string
  title: string
  specialty: string
  region_id: string
  region_name: string
  phone: string
  family_count: number
  workload: number
  status: 'active' | 'disabled'
  bio: string
  joined_at: string
}

export interface PlanSeed extends EntityRecord {
  plan_no: string
  user_id: string
  user_name: string
  family_id: string
  family_name: string
  drug_id: string
  drug_name: string
  dosage: string
  frequency: string
  timing: string[]
  start_date: string
  end_date: string
  owner_id: string
  owner_name: string
  status: 'active' | 'paused' | 'completed' | 'overdue'
  pause_reason: string
  adherence: number
  next_remind_at: string
  created_at: string
  updated_at: string
}

export interface RecordSeed extends EntityRecord {
  plan_id: string
  plan_no: string
  user_id: string
  user_name: string
  family_name: string
  drug_name: string
  dosage: string
  scheduled_at: string
  taken_at: string
  status: 'taken' | 'overdue' | 'skipped' | 'pending'
  source: string
  recorded_by: string
  note: string
  corrected_from: string
  owner_name: string
}

export interface AlertRuleSeed extends EntityRecord {
  name: string
  metric_type: string
  lower_bound: number
  upper_bound: number
  duration_minutes: number
  audience: string
  severity: '低' | '中' | '高'
  channels: string[]
  status: 'active' | 'disabled'
  version: number
  updated_at: string
}

export interface AlertSeed extends EntityRecord {
  alert_no: string
  user_id: string
  user_name: string
  family_id: string
  family_name: string
  rule_name: string
  alert_type: string
  metric_value: string
  reference_range: string
  severity: '低' | '中' | '高'
  status: 'pending' | 'processing' | 'resolved' | 'closed'
  owner_id: string
  owner_name: string
  triggered_at: string
  sla_due_at: string
  closed_at: string
  close_reason: string
}

export interface ReportSeed extends EntityRecord {
  report_no: string
  user_id: string
  user_name: string
  family_name: string
  period_start: string
  period_end: string
  report_type: '周报' | '月报' | '季度报告'
  template_name: string
  status: 'generating' | 'completed' | 'failed' | 'sent'
  generated_at: string
  sent_at: string
  failure_reason: string
  asset_name: string
}

export interface DeviceSeed extends EntityRecord {
  device_no: string
  model_name: string
  device_type: string
  user_id: string
  user_name: string
  family_id: string
  family_name: string
  region_id: string
  region_name: string
  status: 'online' | 'offline' | 'fault' | 'unbound'
  battery: number
  firmware: string
  last_report_at: string
  bound_at: string
  fault_note: string
}

export interface AdminSeed extends EntityRecord {
  admin_no: string
  name: string
  account: string
  phone: string
  email: string
  role_codes: string[]
  role_names: string[]
  data_scope: string
  data_scope_label: string
  region_scope: string
  status: 'active' | 'disabled'
  mfa_enabled: boolean
  last_login_at: string
  login_history: Array<{ at: string; ip: string; result: string; device: string }>
}

export const REGION_SEED: RegionSeed[] = [
  ['r-sh', '310000', '上海市', 'province', '', '', '上海市', 1],
  ['r-sh-city', '310100', '上海市', 'city', 'r-sh', '上海市', '上海市 / 上海市', 1],
  ['r-pd', '310115', '浦东新区', 'district', 'r-sh-city', '上海市', '上海市 / 上海市 / 浦东新区', 1],
  ['r-xh', '310104', '徐汇区', 'district', 'r-sh-city', '上海市', '上海市 / 上海市 / 徐汇区', 2],
  ['r-ja', '310106', '静安区', 'district', 'r-sh-city', '上海市', '上海市 / 上海市 / 静安区', 3],
  ['r-cn', '310105', '长宁区', 'district', 'r-sh-city', '上海市', '上海市 / 上海市 / 长宁区', 4],
  ['r-mh', '310112', '闵行区', 'district', 'r-sh-city', '上海市', '上海市 / 上海市 / 闵行区', 5],
  ['r-bs', '310113', '宝山区', 'district', 'r-sh-city', '上海市', '上海市 / 上海市 / 宝山区', 6],
  ['r-js', '320000', '江苏省', 'province', '', '', '江苏省', 2],
  ['r-nj', '320100', '南京市', 'city', 'r-js', '江苏省', '江苏省 / 南京市', 1],
  ['r-gl', '320106', '鼓楼区', 'district', 'r-nj', '南京市', '江苏省 / 南京市 / 鼓楼区', 1],
  ['r-sz', '320500', '苏州市', 'city', 'r-js', '江苏省', '江苏省 / 苏州市', 2],
  ['r-gs', '320508', '姑苏区', 'district', 'r-sz', '苏州市', '江苏省 / 苏州市 / 姑苏区', 1],
  ['r-zj', '330000', '浙江省', 'province', '', '', '浙江省', 3],
  ['r-hz', '330100', '杭州市', 'city', 'r-zj', '浙江省', '浙江省 / 杭州市', 1],
  ['r-xihu', '330106', '西湖区', 'district', 'r-hz', '杭州市', '浙江省 / 杭州市 / 西湖区', 1],
  ['r-nb', '330200', '宁波市', 'city', 'r-zj', '浙江省', '浙江省 / 宁波市', 2],
  ['r-yz', '330212', '鄞州区', 'district', 'r-nb', '宁波市', '浙江省 / 宁波市 / 鄞州区', 1],
].map((item, index) => ({
  id: String(item[0]),
  code: String(item[1]),
  name: String(item[2]),
  level: item[3] as RegionSeed['level'],
  level_label: item[3] === 'province' ? '省级' : item[3] === 'city' ? '市级' : '区县级',
  parent_id: String(item[4]),
  parent_name: String(item[5] || '—'),
  path: String(item[6]),
  sort: Number(item[7]),
  status: 'active',
  resource_count: 0,
  admin_scope_count: 0,
  created_at: isoDaysAgo(420 + index, 9, 30),
  updated_at: isoDaysAgo(index + 3, 10, 15),
  row_version: 1,
}))

export const DISTRICT_REGIONS = REGION_SEED.filter(region => region.level === 'district')

export const STREETS = ['锦绣路', '龙阳路', '漕溪北路', '南京西路', '天山路', '莘庄路', '牡丹江路', '中山北路', '珠江路', '干将东路', '文三路', '四明中路']

export const USER_NAMES = [
  '王秀兰', '李建国', '张桂芳', '刘志强', '陈美华', '杨国平', '赵淑珍', '黄志明', '周桂英', '吴建华',
  '徐秀英', '孙德明', '胡丽娟', '朱国强', '高玉珍', '林志远', '何秀芳', '郭建平', '马桂兰', '罗国华',
  '梁美玲', '宋建明', '郑秀珍', '谢志刚', '韩桂芳', '唐建华', '冯秀云', '于德海', '董丽华', '萧建国',
  '程淑华', '曹志伟', '袁桂英', '邓国平', '许美珍', '傅建东', '沈秀华', '曾志强', '彭桂香', '吕国梁',
  '苏玉梅', '卢建华', '蒋秀兰', '蔡志明', '贾桂珍', '丁国华', '魏美英', '薛建平', '叶秀珍', '阎志刚',
]

const FAMILY_SURNAMES = ['王', '李', '张', '刘', '陈', '杨', '赵', '黄', '周', '吴', '徐', '孙', '胡', '朱', '高', '林']

export const HEALTH_TAGS = ['高血压', '2型糖尿病', '冠心病', '骨质疏松', '慢阻肺', '高血脂', '房颤', '认知障碍']

export const FAMILY_TYPES = ['核心家庭', '三代同堂', '夫妻家庭', '独居长者']

export const FAMILY_ROLES = ['本人', '配偶', '子女', '父母', '其他亲属']

export const BLOOD_TYPES = ['A型', 'B型', 'O型', 'AB型']

export const MEDICAL_HISTORY = ['高血压十余年，规律服药', '2型糖尿病，饮食控制', '冠心病支架术后', '骨质疏松，定期复查', '慢阻肺，秋冬易加重', '高血脂，控制饮食', '房颤，长期抗凝', '认知功能减退，家属照护']

export const ALLERGY_HISTORY = ['青霉素过敏', '磺胺类过敏', '花粉过敏', '海鲜过敏', '无已知过敏']

export function buildUsers(): UserSeed[] {
  const random = mulberry32(20261017)
  return USER_NAMES.map((name, index) => {
    const region = DISTRICT_REGIONS[index % DISTRICT_REGIONS.length] as RegionSeed
    const phonePrefix = ['138', '139', '137'][index % 3] as string
    const birthYear = index < 30 ? intBetween(random, 1938, 1958) : intBetween(random, 1959, 1988)
    const month = intBetween(random, 1, 12)
    const day = intBetween(random, 1, 28)
    const status = pickBy(random, ['active', 'active', 'active', 'pending_verification', 'disabled', 'cancelled'] as const, value =>
      value === 'active' ? 8 : value === 'pending_verification' ? 2 : 1,
    )
    const risk = index % 9 === 0 ? '高风险' : index % 4 === 0 ? '中风险' : '低风险'
    const tags = index % 3 === 0 ? [HEALTH_TAGS[index % HEALTH_TAGS.length] as string] : index % 5 === 0 ? [HEALTH_TAGS[index % 4] as string, HEALTH_TAGS[(index + 3) % 8] as string] : index % 7 === 0 ? [HEALTH_TAGS[(index + 2) % 8] as string] : []
    return {
      id: `user-${index + 1}`,
      user_no: `KH2026${String(index + 1).padStart(4, '0')}`,
      name,
      phone: `${phonePrefix}${String(10000000 + index * 137).slice(0, 8)}`,
      birth_date: `${birthYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      gender: index % 2 === 0 ? 'female' : 'male',
      region_id: region.id,
      region_name: region.name,
      region_path: region.path,
      family_id: index < 44 ? `family-${Math.floor(index / 2) + 1}` : '',
      family_name: index < 44 ? `${FAMILY_SURNAMES[Math.floor(index / 2) % FAMILY_SURNAMES.length]}家` : '',
      family_role: FAMILY_ROLES[index % FAMILY_ROLES.length] as string,
      family_type: FAMILY_TYPES[index % FAMILY_TYPES.length] as string,
      age: 2026 - birthYear,
      status,
      risk_level: risk,
      tags,
      device_bound: index % 4 !== 3,
      address: `${region.path.split(' / ')[0]}${pick(random, STREETS)}${intBetween(random, 10, 998)}弄${intBetween(random, 1, 26)}号${intBetween(random, 101, 1802)}室`,
      last_active_at: status === 'active' ? isoHoursAgo(intBetween(random, 0, 72), intBetween(random, 0, 59)) : isoDaysAgo(intBetween(random, 4, 50), 10, 20),
      registered_at: isoDaysAgo(intBetween(random, 20, 900), intBetween(random, 8, 20), intBetween(random, 0, 59)),
      height_cm: intBetween(random, 150, 182),
      weight_kg: intBetween(random, 45, 86),
      blood_type: pick(random, BLOOD_TYPES),
      medical_history: pick(random, MEDICAL_HISTORY),
      allergy_history: pick(random, ALLERGY_HISTORY),
      emergency_contact: `${FAMILY_SURNAMES[(index + 3) % FAMILY_SURNAMES.length]}女士 139${String(20000000 + index * 211).slice(0, 8)}`,
      consent_scope: index % 6 === 0 ? ['健康数据共享'] : ['健康数据共享', '家庭成员查看'],
      created_at: isoDaysAgo(intBetween(random, 20, 900), intBetween(random, 8, 20), intBetween(random, 0, 59)),
      updated_at: isoDaysAgo(intBetween(random, 0, 40), 11, 5),
      row_version: intBetween(random, 1, 6),
    }
  })
}

export function buildFamilies(users: UserSeed[]): FamilySeed[] {
  const random = mulberry32(20261018)
  const families: FamilySeed[] = []
  for (let index = 1; index <= 24; index += 1) {
    const members = users.slice((index - 1) * 2, (index - 1) * 2 + 2)
    const owner = members[0] as UserSeed
    const region = DISTRICT_REGIONS[index % DISTRICT_REGIONS.length] as RegionSeed
    const surname = FAMILY_SURNAMES[(index - 1) % FAMILY_SURNAMES.length] as string
    families.push({
      id: `family-${index}`,
      family_no: `JT2026${String(index).padStart(4, '0')}`,
      name: `${surname}家`,
      owner_user_id: owner.id,
      owner_name: owner.name,
      family_type: FAMILY_TYPES[index % FAMILY_TYPES.length] as string,
      region_id: region.id,
      region_name: region.name,
      region_path: region.path,
      address: owner.address,
      member_count: index === 9 ? 1 : intBetween(random, 2, 5),
      guardian_count: index === 9 ? 1 : index % 7 === 0 ? 1 : intBetween(random, 2, 4),
      risk_level: index % 11 === 0 ? '高风险' : index % 4 === 0 ? '中风险' : '低风险',
      status: index === 22 ? 'disabled' : 'active',
      created_at: isoDaysAgo(intBetween(random, 30, 700), 9, 20),
      updated_at: isoDaysAgo(intBetween(random, 0, 28), 14, 10),
      row_version: intBetween(random, 1, 4),
    })
  }
  for (const user of users) {
    if (user.family_id) {
      const family = families.find(item => item.id === user.family_id)
      if (family) {
        user.family_name = family.name
        family.member_count = Math.max(family.member_count, 1)
      }
    }
  }
  return families
}

export const DRUG_CATALOG: Array<[string, string, string, string, string]> = [
  ['阿司匹林肠溶片', '拜阿司匹灵', '心脑血管', '片剂', '100mg×30片'],
  ['硝苯地平控释片', '拜新同', '心脑血管', '片剂', '30mg×7片'],
  ['苯磺酸氨氯地平片', '络活喜', '心脑血管', '片剂', '5mg×28片'],
  ['缬沙坦胶囊', '代文', '心脑血管', '胶囊', '80mg×14粒'],
  ['氯沙坦钾片', '科素亚', '心脑血管', '片剂', '50mg×7片'],
  ['琥珀酸美托洛尔缓释片', '倍他乐克', '心脑血管', '片剂', '47.5mg×7片'],
  ['阿托伐他汀钙片', '立普妥', '心脑血管', '片剂', '20mg×7片'],
  ['瑞舒伐他汀钙片', '可定', '心脑血管', '片剂', '10mg×28片'],
  ['硫酸氢氯吡格雷片', '波立维', '心脑血管', '片剂', '75mg×7片'],
  ['利伐沙班片', '拜瑞妥', '心脑血管', '片剂', '10mg×5片'],
  ['单硝酸异山梨酯缓释片', '依姆多', '心脑血管', '片剂', '60mg×7片'],
  ['盐酸二甲双胍缓释片', '格华止', '降糖用药', '片剂', '0.5g×30片'],
  ['格列美脲片', '亚莫利', '降糖用药', '片剂', '2mg×30片'],
  ['门冬胰岛素注射液', '诺和锐', '降糖用药', '注射剂', '3ml:300U'],
  ['甘精胰岛素注射液', '来得时', '降糖用药', '注射剂', '3ml:300U'],
  ['奥美拉唑肠溶胶囊', '洛赛克', '消化系统', '胶囊', '20mg×14粒'],
  ['雷贝拉唑钠肠溶片', '波利特', '消化系统', '片剂', '10mg×7片'],
  ['铝碳酸镁咀嚼片', '达喜', '消化系统', '片剂', '0.5g×20片'],
  ['蒙脱石散', '思密达', '消化系统', '散剂', '3g×10袋'],
  ['双歧杆菌三联活菌胶囊', '培菲康', '消化系统', '胶囊', '210mg×36粒'],
  ['布洛芬缓释胶囊', '芬必得', '镇痛解热', '胶囊', '0.3g×20粒'],
  ['对乙酰氨基酚片', '必理通', '镇痛解热', '片剂', '0.5g×10片'],
  ['氨溴索口服液', '沐舒坦', '呼吸系统', '口服液', '100ml'],
  ['孟鲁司特钠片', '顺尔宁', '呼吸系统', '片剂', '10mg×5片'],
  ['氯雷他定片', '开瑞坦', '呼吸系统', '片剂', '10mg×6片'],
  ['盐酸西替利嗪片', '仙特明', '呼吸系统', '片剂', '10mg×12片'],
  ['沙丁胺醇气雾剂', '万托林', '呼吸系统', '气雾剂', '100μg×200揿'],
  ['布地奈德福莫特罗吸入粉雾剂', '信必可都保', '呼吸系统', '吸入粉雾剂', '160μg:4.5μg×60吸'],
  ['碳酸钙D3片', '钙尔奇', '骨骼营养', '片剂', '600mg×60片'],
  ['阿仑膦酸钠片', '福善美', '骨骼营养', '片剂', '70mg×1片'],
  ['骨化三醇软胶囊', '罗盖全', '骨骼营养', '胶囊', '0.25μg×10粒'],
  ['甲钴胺片', '弥可保', '神经维生素', '片剂', '0.5mg×20片'],
  ['维生素B1片', '华中药业', '神经维生素', '片剂', '10mg×100片'],
  ['叶酸片', '斯利安', '神经维生素', '片剂', '0.4mg×31片'],
  ['氯化钾缓释片', '补达秀', '神经维生素', '片剂', '0.5g×24片'],
  ['复方丹参滴丸', '天士力', '中成药', '滴丸', '27mg×180丸'],
  ['银杏叶片', '扬子江', '中成药', '片剂', '9.6mg×24片'],
  ['血塞通软胶囊', '三七总皂苷', '中成药', '胶囊', '100mg×30粒'],
  ['六味地黄丸', '同仁堂', '中成药', '丸剂', '360丸'],
  ['藿香正气口服液', '太极', '中成药', '口服液', '10ml×10支'],
  ['复方甘草片', '华润三九', '呼吸系统', '片剂', '100片'],
  ['左氧氟沙星片', '可乐必妥', '抗感染', '片剂', '0.5g×5片'],
  ['厄贝沙坦片', '安博维', '心脑血管', '片剂', '150mg×7片'],
  ['非洛地平缓释片', '波依定', '心脑血管', '片剂', '5mg×10片'],
  ['西格列汀片', '捷诺维', '降糖用药', '片剂', '100mg×14片'],
]

const MANUFACTURERS = ['华东制药', '同仁堂', '恒瑞医药', '云南白药', '石药集团', '扬子江药业', '齐鲁制药', '正大天晴', '哈药集团', '修正药业']
const COVER_COLORS = ['#DDF3EA', '#E8F1FD', '#FDF0DD', '#F7E8E8', '#EDE8FB', '#E2F4F6']
/** Indications are grouped by the drug's own category so prototype records stay clinically coherent. */
const CATEGORY_INDICATIONS: Record<string, string[]> = {
  心脑血管: ['用于高血压、冠心病的长期治疗与二级预防', '用于原发性高血压，降低心脑血管事件风险', '用于慢性稳定型心绞痛及心肌缺血的长期管理'],
  降糖用药: ['用于 2 型糖尿病患者的血糖控制', '用于改善餐后血糖，需配合饮食与运动管理', '用于二甲双胍控制不佳的 2 型糖尿病联合治疗'],
  消化系统: ['用于胃酸过多、反酸与胃部烧灼感', '用于急慢性腹泻及肠道菌群失调', '用于胃溃疡、十二指肠溃疡的规范治疗'],
  呼吸系统: ['用于缓解支气管痉挛与喘息症状', '用于过敏性鼻炎及荨麻疹相关症状', '用于痰液黏稠、排痰困难的祛痰治疗'],
  抗感染: ['用于敏感菌所致的呼吸道感染', '用于敏感菌所致的泌尿系统感染', '用于皮肤软组织感染，需按疗程规范使用'],
  解热镇痛: ['用于缓解轻至中度疼痛，如头痛、牙痛与肌肉痛', '用于普通感冒或流感引起的发热', '用于骨关节炎的短期疼痛管理'],
  骨骼营养: ['用于骨质疏松及钙质补充', '用于绝经后妇女骨质疏松的防治', '用于维生素 D 缺乏相关骨代谢异常'],
  神经维生素: ['用于周围神经病变引起的感觉异常', '用于维生素 B 族缺乏的预防与治疗', '用于神经功能改善与营养支持'],
  中成药: ['用于气滞血瘀所致的胸闷、心悸', '用于活血化瘀、通脉止痛', '用于肝肾阴虚引起的头晕耳鸣，需辨证使用'],
}
const CATEGORY_USAGES: Record<string, string> = {
  心脑血管: '口服，每日一次，每次一片，晨起后服用并监测血压',
  降糖用药: '口服，每日一次，随餐或餐后服用，按血糖监测结果调整',
  消化系统: '口服，每日一次，餐前半小时服用，避免与同类药物重复使用',
  呼吸系统: '遵医嘱使用，症状缓解后逐步减量，吸入制剂使用后漱口',
  抗感染: '口服，按医嘱完成整个疗程，不可自行停药或减量',
  解热镇痛: '口服，疼痛或发热时使用，两次用药至少间隔 6 小时',
  骨骼营养: '口服，每日一次，餐后服用并保证充足日照',
  神经维生素: '口服，每日一次，餐后服用，长期使用需定期复查',
  中成药: '口服，每日一次，餐后温水送服，服药期间忌食生冷油腻',
}
const PRECAUTIONS = ['肝肾功能不全者慎用，定期复查', '服药期间注意监测血压与心率', '孕妇及哺乳期妇女禁用', '如出现皮疹或明显不适请立即停药并就医']

export function buildDrugs(): DrugSeed[] {
  const random = mulberry32(3576)
  return DRUG_CATALOG.map((item, index) => {
    const [genericName, tradeName, category, dosageForm, spec] = item
    const indications = CATEGORY_INDICATIONS[category] ?? CATEGORY_INDICATIONS['中成药'] ?? []
    const stock = index % 9 === 0 ? intBetween(random, 8, 40) : intBetween(random, 120, 1800)
    const reviewStatus = index % 13 === 0 ? 'pending' : index % 17 === 0 ? 'rejected' : index % 19 === 0 ? 'offline' : 'approved'
    return {
      id: `drug-${index + 1}`,
      drug_no: `YP2026${String(index + 1).padStart(4, '0')}`,
      generic_name: genericName,
      trade_name: tradeName,
      approval_no: `国药准字H${2020 + (index % 6)}${String(1000 + index)}`,
      barcode: `69${String(10000000000 + index * 7919).slice(0, 11)}`,
      category_id: `cat-${category}`,
      category_name: category,
      otc_type: index % 5 === 0 ? '非处方药' : '处方药',
      dosage_form: dosageForm,
      spec,
      unit: dosageForm === '注射剂' ? '支' : dosageForm === '口服液' ? '瓶' : '盒',
      manufacturer: MANUFACTURERS[index % MANUFACTURERS.length] as string,
      indication: indications[index % indications.length] as string,
      usage: CATEGORY_USAGES[category] ?? '口服，遵医嘱使用，出现不适请及时就医',
      precautions: PRECAUTIONS[index % PRECAUTIONS.length] as string,
      stock,
      safety_stock: index % 3 === 0 ? 60 : 120,
      price_fen: intBetween(random, 1280, 16800),
      review_status: reviewStatus,
      image_name: `${genericName}.png`,
      cover_color: COVER_COLORS[index % COVER_COLORS.length] as string,
      created_at: isoDaysAgo(intBetween(random, 40, 600), 9, 15),
      updated_at: isoDaysAgo(intBetween(random, 0, 30), 13, 40),
      row_version: intBetween(random, 1, 5),
    }
  })
}

export function buildCareTeam(): CareSeed[] {
  const random = mulberry32(777)
  const people: Array<[string, string, string]> = [
    ['张明远', '主任医师', '心血管内科'], ['李晓岚', '副主任医师', '内分泌科'], ['周敏', '健康管理师', '慢病管理'],
    ['吴海峰', '副主任医师', '呼吸内科'], ['陈静', '营养师', '临床营养'], ['郑康', '康复师', '运动康复'],
    ['孙悦', '健康管理师', '家庭照护'], ['刘承志', '主任医师', '老年医学'], ['何雨薇', '药师', '临床药学'],
    ['唐立', '副主任医师', '神经内科'], ['顾晓晴', '健康管理师', '健康随访'], ['冯磊', '康复师', '心肺康复'],
    ['蒋文', '营养师', '慢病营养'], ['沈安然', '健康管理师', '会员服务'],
  ]
  return people.map((person, index) => {
    const region = DISTRICT_REGIONS[index % DISTRICT_REGIONS.length] as RegionSeed
    return {
      id: `care-${index + 1}`,
      name: person[0],
      title: person[1],
      specialty: person[2],
      region_id: region.id,
      region_name: region.name,
      phone: `139${String(30000000 + index * 113).slice(0, 8)}`,
      family_count: intBetween(random, 3, 36),
      workload: intBetween(random, 45, 96),
      status: index === 12 ? 'disabled' : 'active',
      bio: `${person[1]}，擅长${person[2]}相关的评估、随访与家庭健康管理。`,
      joined_at: isoDaysAgo(intBetween(random, 90, 900), 9, 15),
      created_at: isoDaysAgo(intBetween(random, 90, 900), 9, 15),
      updated_at: isoDaysAgo(intBetween(random, 0, 40), 10, 30),
      row_version: 1,
    }
  })
}

export function buildPlans(users: UserSeed[], drugs: DrugSeed[], careTeam: CareSeed[]): PlanSeed[] {
  const random = mulberry32(9991)
  const approvedDrugs = drugs.filter(drug => drug.review_status === 'approved')
  const families = Array.from(new Set(users.map(user => user.family_id).filter(Boolean)))
  const frequencies = ['每日一次', '每日两次', '每日三次', '隔日一次', '每周三次']
  const timings = ['早餐后', '午餐后', '晚餐后', '睡前', '晨起空腹']
  return Array.from({ length: 56 }, (_, index) => {
    const user = users[(index * 3 + 1) % users.length] as UserSeed
    const drug = approvedDrugs[(index * 5 + 2) % approvedDrugs.length] as DrugSeed
    const care = careTeam[index % careTeam.length] as CareSeed
    const status = index % 13 === 0 ? 'overdue' : index % 7 === 0 ? 'paused' : index % 11 === 0 ? 'completed' : 'active'
    return {
      id: `plan-${index + 1}`,
      plan_no: `YY2026${String(index + 1).padStart(4, '0')}`,
      user_id: user.id,
      user_name: user.name,
      family_id: families[index % families.length] as string,
      family_name: user.family_name,
      drug_id: drug.id,
      drug_name: drug.generic_name,
      dosage: `${pick(random, ['每次1片', '每次2片', '每次1粒', '每次10ml', '每次1袋'])}`,
      frequency: pick(random, frequencies),
      timing: index % 3 === 0 ? [pick(random, timings), pick(random, timings)] : [pick(random, timings)],
      start_date: isoDaysAgo(intBetween(random, 10, 160), 0, 0).slice(0, 10),
      end_date: isoDaysAgo(-intBetween(random, 20, 180), 0, 0).slice(0, 10),
      owner_id: care.id,
      owner_name: care.name,
      status,
      pause_reason: status === 'paused' ? pick(random, ['出现胃部不适，暂停观察', '家属申请暂停一周', '复查前暂停用药', '药物调整期间暂停']) : '',
      adherence: intBetween(random, 58, 100),
      next_remind_at: isoDaysAgo(-1, intBetween(random, 6, 21), pick(random, [0, 30])),
      created_at: isoDaysAgo(intBetween(random, 10, 160), 9, 20),
      updated_at: isoDaysAgo(intBetween(random, 0, 12), 10, 40),
      row_version: intBetween(random, 1, 4),
    }
  })
}

export function buildRecords(plans: PlanSeed[]): RecordSeed[] {
  const random = mulberry32(5511)
  return Array.from({ length: 76 }, (_, index) => {
    const plan = plans[index % plans.length] as PlanSeed
    const status = index % 11 === 0 ? 'pending' : index % 7 === 0 ? 'skipped' : index % 5 === 0 ? 'overdue' : 'taken'
    const scheduled = isoDaysAgo(intBetween(random, 0, 12), intBetween(random, 6, 21), pick(random, [0, 30]))
    const taken = status === 'taken' ? isoHoursAgo(intBetween(random, 1, 100), intBetween(random, 0, 59)) : ''
    return {
      id: `record-${index + 1}`,
      plan_id: plan.id,
      plan_no: plan.plan_no,
      user_id: plan.user_id,
      user_name: plan.user_name,
      family_name: plan.family_name,
      drug_name: plan.drug_name,
      dosage: plan.dosage,
      scheduled_at: scheduled,
      taken_at: taken,
      status,
      source: status === 'taken' ? pick(random, ['用户确认', '设备同步']) : '系统生成',
      recorded_by: status === 'taken' ? pick(random, ['用户本人', '家属代确认']) : '系统',
      note: status === 'skipped' ? pick(random, ['外出忘记带药', '胃部不适暂缓', '家属要求跳过']) : status === 'overdue' ? '超过提醒时间 2 小时未确认' : '',
      corrected_from: index % 19 === 0 ? '待确认' : '',
      owner_name: plan.owner_name,
      created_at: scheduled,
      updated_at: taken || scheduled,
      row_version: 1,
    }
  })
}

export function buildAlertRules(): AlertRuleSeed[] {
  const random = mulberry32(2026)
  const definitions: Array<[string, string, number, number, string[]]> = [
    ['血压偏高预警', '血压', 90, 140, ['站内信', '短信']],
    ['舒张压偏高预警', '血压', 60, 90, ['站内信']],
    ['空腹血糖偏高', '血糖', 3.9, 7.0, ['站内信', '短信']],
    ['餐后血糖偏高', '血糖', 3.9, 11.1, ['站内信']],
    ['心率异常预警', '心率', 55, 100, ['站内信', '短信']],
    ['血氧偏低预警', '血氧', 94, 100, ['短信', '电话']],
    ['体温异常预警', '体温', 36, 37.3, ['站内信']],
    ['体重快速下降', '体重', 45, 75, ['站内信']],
    ['夜间血压波动', '血压', 85, 135, ['站内信', '短信']],
    ['血糖过低预警', '血糖', 3.9, 6.1, ['短信', '电话']],
    ['静息心率过缓', '心率', 50, 60, ['站内信']],
    ['血氧持续下降', '血氧', 92, 96, ['短信', '电话']],
  ]
  return definitions.map((definition, index) => ({
    id: `rule-${index + 1}`,
    name: definition[0],
    metric_type: definition[1],
    lower_bound: definition[2],
    upper_bound: definition[3],
    duration_minutes: [0, 10, 30, 60][index % 4] as number,
    audience: ['全部用户', '高血压人群', '60岁以上', '高风险家庭'][index % 4] as string,
    severity: index % 5 === 0 ? '高' : index % 3 === 0 ? '中' : '低',
    channels: definition[4],
    status: index === 10 ? 'disabled' : 'active',
    version: intBetween(random, 1, 6),
    updated_at: isoDaysAgo(index + 1, 10, 20),
    created_at: isoDaysAgo(120 + index, 9, 15),
    row_version: 1,
  }))
}

export function buildAlerts(users: UserSeed[], rules: AlertRuleSeed[], careTeam: CareSeed[]): AlertSeed[] {
  const random = mulberry32(8842)
  const metricValues: Record<string, string[]> = {
    血压: ['168/102 mmHg', '152/96 mmHg', '145/92 mmHg', '138/88 mmHg', '176/108 mmHg'],
    血糖: ['9.8 mmol/L', '12.4 mmol/L', '3.4 mmol/L', '7.9 mmol/L', '11.6 mmol/L'],
    心率: ['112 次/分', '48 次/分', '126 次/分', '58 次/分', '104 次/分'],
    血氧: ['91%', '93%', '89%', '95%', '92%'],
    体温: ['38.2℃', '37.8℃', '38.6℃', '37.5℃', '36.2℃'],
    体重: ['42.3 kg', '58.6 kg', '46.1 kg', '72.4 kg', '39.8 kg'],
  }
  return Array.from({ length: 48 }, (_, index) => {
    const user = users[(index * 7 + 3) % users.length] as UserSeed
    const rule = rules[index % rules.length] as AlertRuleSeed
    const status = index % 9 === 0 ? 'closed' : index % 7 === 0 ? 'resolved' : index % 5 === 0 ? 'processing' : 'pending'
    const care = careTeam[index % careTeam.length] as CareSeed
    return {
      id: `alert-${index + 1}`,
      alert_no: `YJ2026${String(index + 1).padStart(4, '0')}`,
      user_id: user.id,
      user_name: user.name,
      family_id: user.family_id,
      family_name: user.family_name,
      rule_name: rule.name,
      alert_type: rule.metric_type,
      metric_value: (metricValues[rule.metric_type] ?? ['异常'])[index % 5] as string,
      reference_range: rule.metric_type === '血压' ? '90-140/60-90 mmHg' : rule.metric_type === '血糖' ? '3.9-7.0 mmol/L' : rule.metric_type === '心率' ? '55-100 次/分' : rule.metric_type === '血氧' ? '95%-100%' : rule.metric_type === '体温' ? '36.0-37.3℃' : '45-75 kg',
      severity: rule.severity,
      status,
      owner_id: status === 'pending' ? '' : care.id,
      owner_name: status === 'pending' ? '' : care.name,
      triggered_at: isoHoursAgo(intBetween(random, 1, 240), intBetween(random, 0, 59)),
      sla_due_at: isoHoursAgo(intBetween(random, -22, 20), 30),
      closed_at: status === 'closed' || status === 'resolved' ? isoHoursAgo(intBetween(random, 0, 20), 15) : '',
      close_reason: status === 'closed' ? pick(random, ['已联系家属并完成复测，指标恢复', '用户已就医，医嘱调整用药', '重复预警，与上一条合并处理']) : '',
      created_at: isoHoursAgo(intBetween(random, 1, 240), intBetween(random, 0, 59)),
      updated_at: isoHoursAgo(intBetween(random, 0, 18), 10),
      row_version: 1,
    }
  })
}

export function buildReports(users: UserSeed[]): ReportSeed[] {
  const random = mulberry32(7102)
  return Array.from({ length: 44 }, (_, index) => {
    const user = users[(index * 5 + 2) % users.length] as UserSeed
    const status = index % 9 === 0 ? 'failed' : index % 7 === 0 ? 'generating' : index % 3 === 0 ? 'sent' : 'completed'
    const type = (['周报', '月报', '季度报告'] as const)[index % 3] as ReportSeed['report_type']
    return {
      id: `report-${index + 1}`,
      report_no: `BG2026${String(index + 1).padStart(4, '0')}`,
      user_id: user.id,
      user_name: user.name,
      family_name: user.family_name,
      period_start: isoDaysAgo(30 - (index % 5), 0, 0).slice(0, 10),
      period_end: isoDaysAgo(1, 0, 0).slice(0, 10),
      report_type: type,
      template_name: type === '周报' ? '家庭健康周报模板' : type === '月报' ? '慢病管理月报模板' : '季度健康评估模板',
      status,
      generated_at: status === 'generating' ? '' : isoHoursAgo(intBetween(random, 2, 240), 20),
      sent_at: status === 'sent' ? isoHoursAgo(intBetween(random, 0, 100), 25) : '',
      failure_reason: status === 'failed' ? '健康指标数据不足，无法生成完整报告' : '',
      asset_name: status === 'completed' || status === 'sent' ? `${user.name}-${type}.pdf` : '',
      created_at: isoDaysAgo(intBetween(random, 1, 40), 9, 30),
      updated_at: isoDaysAgo(intBetween(random, 0, 5), 14, 10),
      row_version: 1,
    }
  })
}

export function buildDevices(users: UserSeed[]): DeviceSeed[] {
  const random = mulberry32(4488)
  const models: Array<[string, string]> = [
    ['康护智能血压计 BP-300', '血压计'], ['康护血糖仪 GM-200', '血糖仪'], ['康护智能手环 W5', '智能手环'],
    ['康护指夹血氧仪 OX-10', '血氧仪'], ['康护智能体脂秤 S3', '体脂秤'], ['康护心电记录仪 ECG-1', '心电记录仪'],
  ]
  return Array.from({ length: 46 }, (_, index) => {
    const model = models[index % models.length] as [string, string]
    const user = users[(index * 3) % users.length] as UserSeed
    const unbound = index % 11 === 0
    const status = unbound ? 'unbound' : index % 9 === 0 ? 'fault' : index % 7 === 0 ? 'offline' : 'online'
    return {
      id: `device-${index + 1}`,
      device_no: `SB2026${String(index + 1).padStart(5, '0')}`,
      model_name: model[0],
      device_type: model[1],
      user_id: unbound ? '' : user.id,
      user_name: unbound ? '' : user.name,
      family_id: unbound ? '' : user.family_id,
      family_name: unbound ? '' : user.family_name,
      region_id: user.region_id,
      region_name: user.region_name,
      status,
      battery: intBetween(random, 8, 100),
      firmware: `v${intBetween(random, 1, 3)}.${intBetween(random, 0, 9)}.${intBetween(random, 0, 9)}`,
      last_report_at: status === 'online' ? isoHoursAgo(intBetween(random, 0, 36), intBetween(random, 0, 59)) : isoHoursAgo(intBetween(random, 48, 500), 10),
      bound_at: unbound ? '' : isoDaysAgo(intBetween(random, 10, 300), 10, 20),
      fault_note: status === 'fault' ? pick(random, ['连续三次数据上报失败', '设备电量异常，疑似电池老化', '蓝牙连接不稳定']) : '',
      created_at: isoDaysAgo(intBetween(random, 30, 500), 9, 10),
      updated_at: isoDaysAgo(intBetween(random, 0, 12), 11, 25),
      row_version: 1,
    }
  })
}

export function buildAdmins(): AdminSeed[] {
  const random = mulberry32(3001)
  const people: Array<[string, string, string[], string, string]> = [
    ['陈知远', 'super_admin', ['super_admin'], 'all', '全部平台数据'],
    ['林珊', 'operations_manager', ['operations_manager'], 'business', '全部业务数据（不含系统安全）'],
    ['张明远', 'health_advisor', ['health_advisor'], 'assigned', '分配家庭与本人跟进数据'],
    ['赵文博', 'drug_librarian', ['drug_librarian'], 'catalog', '药品主数据与库存'],
    ['孙倩', 'drug_reviewer', ['drug_reviewer'], 'review', '待审核药品与审核记录'],
    ['周可欣', 'support_agent', ['support_agent'], 'support', '客服队列与关联用户摘要'],
    ['高远', 'device_ops', ['device_ops'], 'device_region', '所辖区域设备与绑定关系'],
    ['许文', 'analyst', ['analyst'], 'aggregate', '聚合分析（无个人敏感字段）'],
    ['罗静', 'auditor', ['auditor'], 'audit', '审计与运维只读'],
    ['沈嘉禾', 'shenjiahe', ['operations_manager'], 'business', '全部业务数据（不含系统安全）'],
    ['贺明', 'heming', ['health_advisor'], 'assigned', '分配家庭与本人跟进数据'],
    ['段文清', 'duanwenqing', ['drug_librarian'], 'catalog', '药品主数据与库存'],
    ['顾星', 'guxing', ['support_agent'], 'support', '客服队列与关联用户摘要'],
    ['马跃', 'mayue', ['device_ops'], 'device_region', '所辖区域设备与绑定关系'],
  ]
  return people.map((person, index) => {
    const roleName = person[2][0] === 'super_admin' ? '超级管理员' : person[2][0] === 'operations_manager' ? '运营管理员' : person[2][0] === 'health_advisor' ? '健康顾问' : person[2][0] === 'drug_librarian' ? '药品库管理员' : person[2][0] === 'drug_reviewer' ? '药事审核员' : person[2][0] === 'support_agent' ? '客服专员' : person[2][0] === 'device_ops' ? '设备运维' : person[2][0] === 'analyst' ? '数据分析员' : '审计员'
    return {
      id: `admin-${index + 1}`,
      admin_no: `GL2026${String(index + 1).padStart(3, '0')}`,
      name: person[0],
      account: person[1],
      phone: `138${String(60000000 + index * 317).slice(0, 8)}`,
      email: `${person[1]}@kanghu.example`,
      role_codes: person[2],
      role_names: [roleName],
      data_scope: person[3],
      data_scope_label: person[4],
      region_scope: person[3] === 'all' ? '全部区域' : '上海市 / 浦东新区',
      status: index === 13 ? 'disabled' : 'active',
      mfa_enabled: index % 3 !== 0,
      last_login_at: isoHoursAgo(intBetween(random, 1, 200), intBetween(random, 0, 59)),
      login_history: [
        { at: isoHoursAgo(intBetween(random, 1, 24), 10), ip: '10.24.18.32', result: '成功', device: 'Chrome · macOS' },
        { at: isoDaysAgo(intBetween(random, 1, 5), 9, 20), ip: '10.24.18.28', result: '成功', device: 'Edge · Windows' },
        { at: isoDaysAgo(intBetween(random, 6, 20), 8, 40), ip: '10.24.18.11', result: index % 4 === 0 ? '密码错误' : '成功', device: 'Safari · iPad' },
      ],
      created_at: isoDaysAgo(intBetween(random, 80, 700), 9, 30),
      updated_at: isoDaysAgo(intBetween(random, 0, 20), 10, 15),
      row_version: 1,
    }
  })
}

export function buildMessages(): AdminRecord[] {
  const random = mulberry32(6161)
  const types = ['健康提醒', '用药提醒', '会员通知', '系统公告', '服务关怀']
  const channels = [['站内信'], ['站内信', '短信'], ['站内信', '微信服务号'], ['短信']]
  const audienceTypes = ['全部用户', '指定标签', '指定家庭', '指定用户']
  return Array.from({ length: 36 }, (_, index) => {
    const status = index % 10 === 0 ? 'failed' : index % 9 === 0 ? 'cancelled' : index % 7 === 0 ? 'draft' : index % 6 === 0 ? 'scheduled' : index % 5 === 0 ? 'sending' : 'sent'
    const type = types[index % types.length] as string
    return {
      id: `message-${index + 1}`,
      message_no: `XX2026${String(index + 1).padStart(4, '0')}`,
      title: `${type}：${['秋季血压管理提醒', '用药时间调整通知', '会员权益到期提醒', '系统升级维护公告', '家庭照护周报已生成'][index % 5]}`,
      message_type: type,
      template_name: `${type}标准模板`,
      channels: channels[index % channels.length] as string[],
      audience_type: audienceTypes[index % audienceTypes.length] as string,
      audience_label: `${intBetween(random, 30, 1800)} 位用户`,
      content: '尊敬的用户，您的家庭健康档案已完成本周更新。请及时查看用药与健康提醒，如有不适请联系健康顾问。',
      status,
      scheduled_at: status === 'scheduled' ? isoHoursAgo(-intBetween(random, 2, 48), 0) : isoHoursAgo(intBetween(random, 2, 100), 20),
      sent_at: status === 'sent' ? isoHoursAgo(intBetween(random, 1, 90), 15) : '',
      delivered: status === 'sent' ? intBetween(random, 25, 1700) : 0,
      read_count: status === 'sent' ? intBetween(random, 10, 900) : 0,
      failed_count: status === 'failed' ? intBetween(random, 4, 80) : 0,
      created_by: '林珊',
      created_at: isoDaysAgo(intBetween(random, 1, 35), 9, 20),
      updated_at: isoDaysAgo(intBetween(random, 0, 6), 11, 30),
      row_version: 1,
    }
  })
}

export function buildTemplates(): AdminRecord[] {
  const random = mulberry32(5151)
  const types = ['健康提醒', '用药提醒', '会员通知', '系统公告', '服务关怀']
  return Array.from({ length: 20 }, (_, index) => ({
    id: `template-${index + 1}`,
    name: `${types[index % types.length]}模板${index + 1}`,
    template_type: types[index % types.length] as string,
    channels: index % 3 === 0 ? ['站内信', '短信'] : index % 3 === 1 ? ['站内信', '微信服务号'] : ['站内信'],
    title_template: '{{用户姓名}}，{{提醒类型}}提醒',
    content_template: '{{用户姓名}}您好，您的{{计划名称}}将在{{提醒时间}}进行，请按医嘱服药。',
    variables: ['用户姓名', '提醒类型', '计划名称', '提醒时间'],
    link_name: index % 4 === 0 ? '首页健康概览' : '用药计划页',
    status: index === 18 ? 'disabled' : 'active',
    version: intBetween(random, 1, 5),
    updated_at: isoDaysAgo(index + 1, 10, 30),
    created_at: isoDaysAgo(100 + index, 9, 10),
    row_version: 1,
  }))
}

export function buildTips(): AdminRecord[] {
  const random = mulberry32(8123)
  const categories = ['饮食营养', '运动康复', '用药安全', '慢病管理', '心理健康']
  const audiences = ['全部用户', '高血压人群', '60岁以上', '高风险家庭', '糖尿病用户']
  return Array.from({ length: 36 }, (_, index) => ({
    id: `tip-${index + 1}`,
    title: ['秋季血压波动的日常管理', '老年人用药安全的五个细节', '低盐饮食如何兼顾口味', '饭后散步的正确时长', '血糖监测的时间选择', '家庭照护者的情绪调节', '防跌倒家居改造清单', '感冒药与慢病用药的相互作用'][index % 8] + (index > 7 ? `（${Math.floor(index / 8) + 1}）` : ''),
    category: categories[index % categories.length] as string,
    summary: '结合家庭健康档案与用药记录，为照护者提供可执行的日常建议。',
    content: '建议每日固定时间测量并记录指标，出现明显异常时先复测，再联系健康顾问。饮食、运动和用药调整应在专业人员指导下进行。',
    cover_name: `健康贴士封面-${(index % 8) + 1}.png`,
    cover_color: ['#DDF3EA', '#E8F1FD', '#FDF0DD', '#F7E8E8'][index % 4] as string,
    audience: audiences[index % audiences.length] as string,
    link_name: '首页健康概览',
    sort: index + 1,
    status: index % 9 === 0 ? 'offline' : index % 7 === 0 ? 'scheduled' : 'online',
    online_at: isoDaysAgo(intBetween(random, 1, 30), 9, 0),
    offline_at: isoDaysAgo(-intBetween(random, 10, 90), 23, 59),
    views: intBetween(random, 80, 9800),
    updated_at: isoDaysAgo(intBetween(random, 0, 20), 11, 10),
    created_at: isoDaysAgo(intBetween(random, 20, 200), 9, 20),
    row_version: 1,
  }))
}

export function buildFaq(): AdminRecord[] {
  const random = mulberry32(9912)
  const categories = ['账号与认证', '用药提醒', '健康监测', '家庭守护', '会员服务']
  return Array.from({ length: 30 }, (_, index) => ({
    id: `faq-${index + 1}`,
    question: ['如何为家人建立健康档案？', '用药提醒没有按时收到怎么办？', '设备数据多久同步一次？', '如何邀请家人加入家庭守护？', '会员权益包含哪些服务？', '如何修改绑定的手机号？'][index % 6] + (index > 5 ? `（${Math.floor(index / 6) + 1}）` : ''),
    category_name: categories[index % categories.length] as string,
    answer: '进入个人中心后选择对应服务，按照页面提示完成操作。如仍有疑问，可通过帮助中心提交反馈，客服将在工作时间内回复。',
    link_name: index % 3 === 0 ? '个人中心' : '帮助中心',
    sort: index + 1,
    status: index % 10 === 0 ? 'offline' : 'published',
    published_at: isoDaysAgo(intBetween(random, 1, 120), 10, 0),
    views: intBetween(random, 30, 4600),
    updated_at: isoDaysAgo(intBetween(random, 0, 20), 15, 20),
    created_at: isoDaysAgo(intBetween(random, 30, 300), 9, 40),
    row_version: 1,
  }))
}

export function buildBenefits(): AdminRecord[] {
  const names: Array<[string, string]> = [
    ['专属健康顾问', 'MEMBER_ADVISOR'], ['月度健康报告', 'MEMBER_REPORT'], ['优先客服响应', 'MEMBER_SUPPORT'],
    ['家庭健康档案', 'MEMBER_FAMILY'], ['用药提醒短信', 'MEMBER_SMS'], ['年度健康评估', 'MEMBER_ASSESS'],
    ['专家在线咨询', 'MEMBER_CONSULT'], ['设备延保服务', 'MEMBER_DEVICE'], ['健康课程', 'MEMBER_COURSE'],
  ]
  return names.map((item, index) => ({
    id: `benefit-${index + 1}`,
    name: item[0],
    code: item[1],
    description: `${item[0]}，会员有效期内按方案约定提供。`,
    plan_count: index % 3 + 1,
    status: index === 8 ? 'disabled' : 'active',
    created_at: isoDaysAgo(200 - index, 9, 0),
    updated_at: isoDaysAgo(index + 1, 11, 0),
    row_version: 1,
  }))
}

export function buildMembershipPlans(): AdminRecord[] {
  const random = mulberry32(4141)
  const definitions: Array<[string, number, number, string]> = [
    ['基础守护月卡', 1, 2900, '专属健康顾问、月度健康报告'], ['家庭守护季卡', 3, 7900, '家庭健康档案、优先客服响应、用药提醒短信'],
    ['家庭守护年卡', 12, 26800, '全部基础权益、年度健康评估、专家在线咨询'], ['长辈安心月卡', 1, 3900, '专属健康顾问、用药提醒短信'],
    ['慢病管理季卡', 3, 9900, '月度健康报告、专家在线咨询、健康课程'], ['慢病管理年卡', 12, 32800, '全部慢病权益、设备延保服务'],
    ['银龄关怀年卡', 12, 19800, '优先客服响应、家庭健康档案、健康课程'], ['体验月卡', 1, 900, '月度健康报告'],
  ]
  return definitions.map((item, index) => ({
    id: `mplan-${index + 1}`,
    plan_no: `HY2026${String(index + 1).padStart(3, '0')}`,
    name: item[0],
    period_months: item[1],
    price_fen: item[2],
    benefit_names: item[3].split('、'),
    description: `${item[0]}，为家庭提供持续、可执行健康照护服务。`,
    image_name: `${item[0]}.png`,
    cover_color: ['#DDF3EA', '#E8F1FD', '#FDF0DD', '#EDE8FB'][index % 4] as string,
    status: index === 7 ? 'offline' : 'online',
    version: intBetween(random, 1, 4),
    sales_count: intBetween(random, 40, 2600),
    updated_at: isoDaysAgo(index + 1, 10, 0),
    created_at: isoDaysAgo(180 - index, 9, 0),
    row_version: 1,
  }))
}

export function buildOrders(users: UserSeed[]): AdminRecord[] {
  const random = mulberry32(5050)
  const plans = ['家庭守护年卡', '基础守护月卡', '慢病管理季卡', '长辈安心月卡', '家庭守护季卡', '银龄关怀年卡']
  return Array.from({ length: 46 }, (_, index) => {
    const user = users[(index * 3 + 4) % users.length] as UserSeed
    const plan = plans[index % plans.length] as string
    const status = index % 13 === 0 ? 'refunded' : index % 11 === 0 ? 'refund_pending' : index % 9 === 0 ? 'closed' : 'paid'
    const amount = [2900, 7900, 26800, 3900, 9900, 19800][index % 6] as number
    // Create orders inside the last 90 days and keep payment/close timestamps
    // on or after creation. This makes the dashboard period totals, order
    // sorting and refund actions internally consistent.
    const createdDaysAgo = (index * 7) % 90 + 1
    const createdHour = 8 + (index * 3) % 14
    const createdMinute = (index * 11) % 60
    const paidDaysAgo = Math.max(0, createdDaysAgo - (index % 2))
    const paidHour = createdHour + (paidDaysAgo === 0 ? 1 : 0)
    const paidAt = status === 'closed'
      ? ''
      : isoDaysAgo(paidDaysAgo, paidHour, createdMinute)
    const createdAt = isoDaysAgo(createdDaysAgo, createdHour, createdMinute)
    const closedAt = status === 'closed'
      ? isoDaysAgo(Math.max(0, createdDaysAgo - 1), Math.min(23, paidHour + 1), 30)
      : ''
    return {
      id: `order-${index + 1}`,
      order_no: `DD2026${String(index + 1).padStart(5, '0')}`,
      user_id: user.id,
      user_name: user.name,
      plan_name: plan,
      amount_fen: amount,
      channel: ['微信支付', '支付宝', '银联'][index % 3] as string,
      status,
      paid_at: paidAt,
      refund_amount_fen: status === 'refunded' || status === 'refund_pending' ? amount : 0,
      refund_reason: status === 'refunded' || status === 'refund_pending' ? pick(random, ['重复购买，申请退款', '服务内容与预期不符', '家庭计划变更', '支付后未使用权益']) : '',
      closed_at: closedAt,
      created_at: createdAt,
      updated_at: status === 'refund_pending' || status === 'refunded'
        ? isoDaysAgo(Math.max(0, createdDaysAgo - 2), 11, 0)
        : closedAt || paidAt,
      row_version: 1,
    }
  })
}

export function buildLinks(): AdminRecord[] {
  const random = mulberry32(7474)
  const pages = ['首页', '用药计划', '健康看板', '家庭档案', '个人中心', '帮助中心', '健康报告', '消息通知']
  return Array.from({ length: 32 }, (_, index) => ({
    id: `link-${index + 1}`,
    name: ['首页健康概览', '用药计划入口', '家庭档案入口', '健康报告入口', '会员中心入口', '帮助中心入口', '设备管理入口', '消息通知入口'][index % 8] + (index > 7 ? `（${Math.floor(index / 8) + 1}）` : ''),
    category_name: ['首页导航', '个人中心', '运营位', '帮助中心'][index % 4] as string,
    target_type: index % 7 === 0 ? '外部链接' : index % 5 === 0 ? '业务动作' : '用户端页面',
    target_page: pages[index % pages.length] as string,
    business_action: index % 5 === 0 ? '打开用药提醒' : '',
    external_url: index % 7 === 0 ? 'https://health.example.com/guide' : '',
    params: index % 6 === 0 ? '{"source":"admin"}' : '{}',
    status: index === 29 ? 'disabled' : 'active',
    usage_count: intBetween(random, 0, 96),
    updated_at: isoDaysAgo(intBetween(random, 0, 30), 11, 0),
    created_at: isoDaysAgo(intBetween(random, 30, 240), 9, 0),
    row_version: 1,
  }))
}

export function buildAssets(): AdminRecord[] {
  const random = mulberry32(9090)
  const names = ['首页健康概览横幅', '用药提醒插画', '专家团队合影', '会员权益说明', '设备使用指南', '健康报告封面', '家庭守护宣传图', '慢病管理课程封面', '帮助中心图标', '品牌标识']
  return Array.from({ length: 34 }, (_, index) => ({
    id: `asset-${index + 1}`,
    name: `${names[index % names.length]}${index > 9 ? `-${Math.floor(index / 10) + 1}` : ''}.${index % 5 === 0 ? 'pdf' : 'png'}`,
    asset_type: index % 5 === 0 ? '文档' : index % 11 === 0 ? '视频' : '图片',
    mime_type: index % 5 === 0 ? 'application/pdf' : index % 11 === 0 ? 'video/mp4' : 'image/png',
    size_kb: intBetween(random, 40, 4200),
    width: index % 5 === 0 ? 0 : intBetween(random, 320, 1440),
    height: index % 5 === 0 ? 0 : intBetween(random, 240, 960),
    folder: ['首页素材', '内容封面', '药品资料', '帮助中心'][index % 4] as string,
    usage_count: intBetween(random, 0, 18),
    status: index === 31 ? 'archived' : 'active',
    cover_color: ['#DDF3EA', '#E8F1FD', '#FDF0DD', '#F7E8E8', '#EDE8FB'][index % 5] as string,
    references: index % 4 === 0 ? '健康贴士、首页装修' : index % 4 === 1 ? '药品资料、通知模板' : index % 4 === 2 ? '帮助中心' : '会员方案',
    uploaded_at: isoDaysAgo(intBetween(random, 1, 120), intBetween(random, 9, 18), intBetween(random, 0, 59)),
    created_at: isoDaysAgo(intBetween(random, 1, 120), intBetween(random, 9, 18), intBetween(random, 0, 59)),
    updated_at: isoDaysAgo(intBetween(random, 0, 20), 10, 30),
    row_version: 1,
  }))
}

export function buildTickets(users: UserSeed[]): AdminRecord[] {
  const random = mulberry32(2211)
  const types = ['用药咨询', '设备故障', '会员支付', '健康报告', '账号问题']
  const priorities = ['低', '中', '高', '紧急']
  const statuses = ['pending', 'assigned', 'processing', 'resolved', 'closed']
  return Array.from({ length: 38 }, (_, index) => {
    const user = users[(index * 5 + 1) % users.length] as UserSeed
    const status = statuses[index % statuses.length] as string
    return {
      id: `ticket-${index + 1}`,
      ticket_no: `GD2026${String(index + 1).padStart(4, '0')}`,
      user_id: user.id,
      user_name: user.name,
      family_name: user.family_name,
      type: types[index % types.length] as string,
      priority: priorities[index % priorities.length] as string,
      status,
      assignee_name: status === 'pending' ? '' : ['周可欣', '顾星', '沈嘉禾'][index % 3] as string,
      title: ['用药提醒时间与实际不符', '血压计无法同步数据', '会员退款进度查询', '健康报告内容缺失', '手机号更换后无法登录'][index % 5] as string,
      description: '用户反馈问题后，客服已记录并创建工单，请按服务规范跟进处理。',
      channel: ['用户端帮助中心', '客服电话', '微信服务号'][index % 3] as string,
      sla_due_at: isoHoursAgo(intBetween(random, -12, 30), 30),
      created_at: isoHoursAgo(intBetween(random, 2, 240), intBetween(random, 0, 59)),
      updated_at: isoHoursAgo(intBetween(random, 0, 20), 10),
      conversation: [
        { role: '用户', at: isoHoursAgo(48, 10), text: '您好，我的提醒时间与设置不一致，麻烦帮忙看一下。' },
        { role: '客服', at: isoHoursAgo(46, 25), text: '已收到您的反馈，正在核对用药计划与提醒设置。' },
        { role: '用户', at: isoHoursAgo(44, 5), text: '好的，谢谢。' },
      ],
      row_version: 1,
    }
  })
}

export function buildFeedback(users: UserSeed[]): AdminRecord[] {
  const random = mulberry32(6162)
  const types = ['功能建议', '内容纠错', '服务投诉', '体验问题']
  const statuses = ['pending', 'processing', 'replied', 'closed']
  return Array.from({ length: 34 }, (_, index) => {
    const user = users[(index * 7 + 2) % users.length] as UserSeed
    const status = statuses[index % statuses.length] as string
    return {
      id: `feedback-${index + 1}`,
      feedback_no: `FK2026${String(index + 1).padStart(4, '0')}`,
      user_id: user.id,
      user_name: user.name,
      type: types[index % types.length] as string,
      source_page: ['首页', '用药计划', '健康报告', '个人中心', '帮助中心'][index % 5] as string,
      content: '希望健康报告可以按周查看，并支持导出给家庭医生。目前查看历史数据需要多次点击。',
      status,
      reply: status === 'replied' || status === 'closed' ? '感谢您的建议，我们已记录并纳入后续优化计划。' : '',
      ticket_no: index % 6 === 0 ? 'GD20260012' : '',
      tags: index % 3 === 0 ? ['产品建议'] : ['体验反馈'],
      created_at: isoHoursAgo(intBetween(random, 2, 260), intBetween(random, 0, 59)),
      closed_at: status === 'closed' ? isoHoursAgo(intBetween(random, 0, 30), 15) : '',
      updated_at: isoHoursAgo(intBetween(random, 0, 20), 11),
      row_version: 1,
    }
  })
}

export function buildAuditLogs(admins: AdminSeed[]): AdminRecord[] {
  const random = mulberry32(8888)
  const modules = ['用户管理', '家庭档案', '用药计划', '健康预警', '药品资料', '消息通知', '会员订单', '系统设置', '权限管理']
  const actions = ['新增', '编辑', '停用', '启用', '导出', '审核通过', '驳回', '关闭', '重置密码']
  return Array.from({ length: 96 }, (_, index) => {
    const admin = admins[index % admins.length] as AdminSeed
    return {
      id: `audit-${index + 1}`,
      created_at: isoHoursAgo(intBetween(random, 1, 360), intBetween(random, 0, 59)),
      actor_name: admin.name,
      actor_account: admin.account,
      module: modules[index % modules.length] as string,
      action: actions[index % actions.length] as string,
      resource_type: modules[index % modules.length] as string,
      resource_label: ['用户 王秀兰', '家庭 王家', '计划 YY20260018', '预警 YJ20260007', '药品 阿司匹林肠溶片', '消息 XX20260012', '订单 DD20260021', '系统参数', '角色 健康顾问'][index % 9] as string,
      result: index % 17 === 0 ? '失败' : '成功',
      ip: `10.24.18.${20 + (index % 30)}`,
      request_no: `REQ-20261017-${String(1000 + index)}`,
      before_json: index % 3 === 0 ? '{"status":"active"}' : '',
      after_json: index % 3 === 0 ? '{"status":"disabled"}' : '',
      detail: index % 17 === 0 ? '目标数据已被其他管理员更新，操作未生效' : '管理操作已完成并写入审计记录',
      row_version: 1,
    }
  })
}

export function buildHealthMetrics(users: UserSeed[]): AdminRecord[] {
  const random = mulberry32(202610)
  const defs: Array<[string, string[], string, string]> = [
    ['血压', ['128/82', '136/88', '148/95', '168/102', '142/90'], 'mmHg', '90-140/60-90'],
    ['血糖', ['5.6', '6.8', '9.8', '12.4', '3.4'], 'mmol/L', '3.9-7.0'],
    ['心率', ['72', '86', '112', '48', '96'], '次/分', '55-100'],
    ['血氧', ['98', '97', '93', '91', '96'], '%', '95-100'],
    ['体温', ['36.5', '36.8', '37.8', '38.2', '36.3'], '℃', '36.0-37.3'],
    ['体重', ['62.5', '58.4', '46.1', '42.3', '70.2'], 'kg', '45-75'],
  ]
  return Array.from({ length: 120 }, (_, index) => {
    const user = users[(index * 3 + 1) % users.length] as UserSeed
    const def = defs[index % defs.length] as [string, string[], string, string]
    const value = def[1][index % 5] as string
    const abnormal = index % 5 >= 2
    return {
      id: `metric-${index + 1}`,
      user_id: user.id,
      user_name: user.name,
      family_name: user.family_name,
      region_name: user.region_name,
      metric_type: def[0],
      metric_value: value,
      value_display: `${value} ${def[2]}`,
      unit: def[2],
      reference_range: def[3],
      measured_at: isoHoursAgo(intBetween(random, 1, 240), intBetween(random, 0, 59)),
      source: index % 4 === 0 ? '用户手动记录' : '设备上报',
      device_name: index % 4 === 0 ? '—' : ['康护智能血压计 BP-300', '康护血糖仪 GM-200', '康护智能手环 W5', '康护指夹血氧仪 OX-10'][index % 4] as string,
      is_abnormal: abnormal,
      severity: abnormal ? (index % 7 === 0 ? '高' : '中') : '低',
      created_at: isoHoursAgo(intBetween(random, 1, 240), intBetween(random, 0, 59)),
      updated_at: isoHoursAgo(intBetween(random, 0, 60), 10),
      row_version: 1,
    }
  })
}
